import { i18n } from "@lingui/core";

import type { Server } from "stoat.js";

import type { OverCapacity } from "@revolt/common";
import type { ChangelogResponse } from "@revolt/modal/modals/Changelog";

/**
 * Build a synthetic changelog/announcement payload for the community-manager
 * over-capacity "pay-or-purge" notice.
 *
 * This reuses the existing changelog/"What's new" modal to surface the notice
 * in-app on next open. Content is Markdown (rendered by the changelog modal),
 * so the upgrade action is a Markdown link. If no upgrade URL is available the
 * pay link is omitted and only the "free up space" guidance is shown — we never
 * invent a checkout URL.
 *
 * @param server Over-capacity server the viewer manages
 * @param over Over-capacity breakdown (messages/storage)
 * @param upgradeUrl Optional upgrade/checkout URL
 */
export function buildOverCapacityNotice(
  server: Server,
  over: OverCapacity,
  upgradeUrl?: string,
): ChangelogResponse {
  const paragraphs: string[] = [];

  paragraphs.push(`**${server.name}**`);

  if (over.messagesOverCapacity && over.storageOverCapacity) {
    paragraphs.push(
      i18n._(
        `This community has reached its message and storage limits. To keep sending messages, upgrade the plan or free up space by removing content.`,
      ),
    );
  } else if (over.storageOverCapacity) {
    paragraphs.push(
      i18n._(
        `This community has reached its storage limit. To keep sending messages, upgrade the plan or free up space by removing content.`,
      ),
    );
  } else {
    paragraphs.push(
      i18n._(
        `This community has reached its message limit. To keep sending messages, upgrade the plan or free up space by removing content.`,
      ),
    );
  }

  if (upgradeUrl) {
    paragraphs.push(`[${i18n._(`Upgrade plan`)}](${upgradeUrl})`);
  }

  return {
    id: `over-capacity:${server.id}`,
    title: i18n._(`Community over capacity`),
    markdown_content: paragraphs.join("\n\n"),
    published_at: new Date().toISOString(),
  };
}
